import { useCallback, useEffect, useRef, useState } from "react"

import { WS_URL } from "../config"
import { ClientboundMessageSchema } from "../protocol"
import type { CommonRoomData, PhaseView } from "../protocol"
import { WS_CLOSE } from "../wsClose"

/**
 * The display's connection to a room.
 *
 * Deliberately not built on GameStateContext/WebSocketProvider. Those are
 * player-shaped: a rejoin token, a `self` to render from, a kick to survive, a
 * draft to preserve. A display has none of that — it has no identity, so there
 * is nothing to restore and nothing to lose. What's left is small enough to say
 * plainly: open a socket, say WatchRoom, hold the last snapshot, and reconnect
 * forever if the room goes quiet.
 *
 * "Reconnect forever" is the one place a TV should differ from a phone. A phone
 * gives up and shows an error a person can act on; a screen on the wall has
 * nobody looking at it, and the room it belongs to may well come back — the game
 * server restarting mid-party shouldn't leave a dead TV that someone has to walk
 * over and reload.
 */

export type TvStatus =
    | { kind: "idle" }
    | { kind: "connecting" }
    | { kind: "watching" }
    | { kind: "reconnecting"; attempt: number }
    /** Terminal: the room doesn't exist, or won't have us. Retrying can't fix it. */
    | { kind: "refused"; reason: string }

export interface TvRoom {
    status: TvStatus
    phase: string | null
    room: CommonRoomData | null
    view: PhaseView | null
}

const INITIAL: TvRoom = { status: { kind: "idle" }, phase: null, room: null, view: null }

/** Backoff for the reconnect loop, in ms. Caps quickly — a TV can afford to keep
 *  knocking, and a room coming back should be picked up within a few seconds. */
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000] as const
const backoffFor = (attempt: number) => BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]

/** Closes the display should give up on rather than retry into a loop. */
const TERMINAL: Partial<Record<number, string>> = {
    [WS_CLOSE.ROOM_NOT_FOUND]: "That room isn't running.",
    [WS_CLOSE.PROTOCOL_ERROR]: "This display was refused by the room.",
    4010: "That room already has as many screens as it allows.", // TOO_MANY_VIEWERS
}

export function useTvRoom(roomCode: string | null): TvRoom {
    const [state, setState] = useState<TvRoom>(INITIAL)

    const socketRef = useRef<WebSocket | null>(null)
    /** The reconnect loop calls back into the opener. Parked in a ref so the two
     *  can reference each other without a dependency cycle — the same shape
     *  WebSocketProvider uses for its own open → close → retry chain. */
    const connectRef = useRef<(code: string) => void>(() => {})
    const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const attemptRef = useRef(0)
    /** Set while tearing down deliberately, so the close handler doesn't treat
     *  our own cleanup as a dropped connection and start reconnecting. */
    const closingRef = useRef(false)

    const connect = useCallback((code: string) => {
        closingRef.current = false
        setState(prev => ({
            ...prev,
            status: attemptRef.current === 0
                ? { kind: "connecting" }
                : { kind: "reconnecting", attempt: attemptRef.current },
        }))

        const socket = new WebSocket(`${WS_URL}/room/${code}`)
        socketRef.current = socket

        socket.onopen = () => {
            // A display announces itself and then never speaks again. The server
            // closes the socket if it does, which is the point.
            socket.send(JSON.stringify({ type: "watch_room" }))
        }

        socket.onmessage = event => {
            let parsed: unknown
            try {
                parsed = JSON.parse(event.data as string)
            } catch {
                return
            }
            const message = ClientboundMessageSchema.safeParse(parsed)
            if (!message.success) {
                // Protocol drift, not a transport problem — log and keep watching
                // rather than tearing down a screen somebody is looking at.
                console.error("TV: unparseable message", message.error)
                return
            }
            if (message.data.type !== "tv_snapshot") return

            attemptRef.current = 0
            setState({
                status: { kind: "watching" },
                phase: message.data.phase,
                room: message.data.room,
                view: message.data.view,
            })
        }

        socket.onclose = event => {
            if (closingRef.current) return
            socketRef.current = null

            const terminal = TERMINAL[event.code]
            if (terminal) {
                setState(prev => ({ ...prev, status: { kind: "refused", reason: terminal } }))
                return
            }

            const attempt = attemptRef.current
            attemptRef.current = attempt + 1
            setState(prev => ({ ...prev, status: { kind: "reconnecting", attempt: attempt + 1 } }))
            retryRef.current = setTimeout(() => connectRef.current(code), backoffFor(attempt))
        }
    }, [])
    connectRef.current = connect

    useEffect(() => {
        if (!roomCode) return

        attemptRef.current = 0
        connect(roomCode)

        return () => {
            closingRef.current = true
            if (retryRef.current) clearTimeout(retryRef.current)
            socketRef.current?.close()
            socketRef.current = null
        }
    }, [roomCode, connect])

    // With no room to watch there is nothing to report — said here rather than by
    // writing INITIAL back from an effect, which would render once with stale
    // state before correcting itself.
    return roomCode ? state : INITIAL
}
